/* eslint-disable sonarjs/no-duplicate-string */
import { formatDatesByMonthYear } from "@ac-expo/utils";
import { compareAsc, format } from "date-fns";
import { fr } from "date-fns/locale";
import { ComponentProps, useState } from "react";
import {
  DateRange,
  DayPickerProps,
  PropsMulti,
  PropsRange,
  PropsSingle,
} from "react-day-picker";
import { Control, FieldError, FieldValues, Path } from "react-hook-form";
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  formInputClassName,
  inputClassName,
  labelClassName,
  useIsMobile,
} from "../../..";
import {
  AcExpoDayPickerMode,
  AcExpoDayPickerPropsBase,
  OnPickTypeDateType,
  isOnPickTypeDateType,
} from "../day-picker";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "./Form";

const formatDateWithFrenchLocale = (date: Date) => format(date, "PPP", { locale: fr });

const getDefaultMonthFromFieldValue = (fieldValue: unknown) => {
  if (
    fieldValue &&
    typeof fieldValue === "object" &&
    ("from" in fieldValue || "to" in fieldValue)
  ) {
    return (fieldValue as DateRange).from;
  }
  return fieldValue as Date | undefined;
};

const getRangeButtonLabel = (fieldValue: DateRange | undefined) => {
  if (!fieldValue) {
    return <span>Selectionner une plage de dates</span>;
  }

  return (
    <span>
      {fieldValue.from ? formatDateWithFrenchLocale(fieldValue.from) : "Start date"}
      {fieldValue.to ? ` - ${formatDateWithFrenchLocale(fieldValue.to)}` : null}
    </span>
  );
};

const getSingleButtonLabel = (fieldValue: Date | undefined) => {
  if (!fieldValue) {
    return <span>Selectionner une date</span>;
  }

  return formatDateWithFrenchLocale(fieldValue);
};

const getMultipleButtonLabel = (fieldValue: Date[] | undefined) => {
  if (fieldValue && fieldValue.length > 0) {
    return formatDatesByMonthYear(...fieldValue);
  }

  return <span>Selectionner une ou plusieurs dates</span>;
};

const getFormDatePickerButtonLabel = (
  mode: AcExpoDayPickerMode,
  fieldValue: unknown,
) => {
  if (mode === "range") {
    return getRangeButtonLabel(fieldValue as DateRange | undefined);
  }

  if (mode === "single") {
    return getSingleButtonLabel(fieldValue as Date | undefined);
  }

  return getMultipleButtonLabel(fieldValue as Date[] | undefined);
};

const getRangeFieldErrorMessages = (error: FieldError) => {
  const rangeError = error as unknown as { from?: FieldError; to?: FieldError };

  return (
    <>
      {error.message}
      {rangeError.from?.message}
      {rangeError.to?.message}
    </>
  );
};

const getNumberOfMonths = (mode: AcExpoDayPickerMode, isMobile: boolean) =>
  mode === "range" && !isMobile ? 2 : 1;

type ModeDayPickerProps<TMode extends AcExpoDayPickerMode> = TMode extends "range"
  ? PropsRange
  : TMode extends "single"
    ? PropsSingle
    : TMode extends "multiple"
      ? PropsMulti
      : never;

type FormDatePickerProps<TForm extends FieldValues, TMode extends AcExpoDayPickerMode> = {
  control: Control<TForm>;
  name: Path<TForm>;
  label?: string;
  hasMessage?: boolean;
  onPick?: OnPickHandler<TMode>;
  onClose?: () => void;
  className?: string;
  disabled?: boolean;
  disabledDays?: DayPickerProps["disabled"];
  popoverProps?: ComponentProps<typeof Popover>;
} & Omit<ModeDayPickerProps<TMode>, "disabled"> &
  AcExpoDayPickerPropsBase;

type OnPickHandler<TMode extends AcExpoDayPickerMode> = (
  date: OnPickTypeDateType<TMode> | undefined,
  fieldOnChange: (date: OnPickTypeDateType<TMode> | undefined) => void,
) => void;

const createOnSelectHandler = <TMode extends AcExpoDayPickerMode>(
  mode: AcExpoDayPickerMode,
  onPick: OnPickHandler<TMode> | undefined,
  onChange: (value: OnPickTypeDateType<TMode> | undefined) => void,
) => {
  return (value: Date | Date[] | DateRange | DateRange[] | undefined) => {
    if (!isOnPickTypeDateType(value, mode)) {
      throw new Error(`Invalid date type ${mode} for value ${value}`);
    }

    if (onPick) {
      onPick(value as OnPickTypeDateType<TMode>, onChange);
      return;
    }

    if (mode === "multiple" && Array.isArray(value)) {
      onChange((value as Date[]).sort(compareAsc) as OnPickTypeDateType<TMode>);
      return;
    }

    onChange(value as OnPickTypeDateType<TMode>);
  };
};

export function FormDatePicker<TForm extends FieldValues, TMode extends AcExpoDayPickerMode>({
  control,
  name,
  label,
  hasMessage = false,
  mode,
  onPick,
  onClose,
  className,
  disabled,
  disabledDays,
  popoverProps,
  ...props
}: FormDatePickerProps<TForm, TMode>) {
  const isMobile = useIsMobile();

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open && onClose) onClose();
    setIsOpen(open);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { value: fieldValue, onChange } = field;
        const onSelect = createOnSelectHandler(mode, onPick, onChange);

        return (
          <FormItem className={cn(formInputClassName, className)}>
            {label && label.length > 0 ? (
              <FormLabel className={labelClassName}>{label}</FormLabel>
            ) : null}

            <Popover {...popoverProps} open={isOpen} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "h-auto min-h-9 w-full justify-start text-wrap pl-3 text-left font-normal text-black",
                      inputClassName,
                      {
                        "border-red-500": fieldState.error,
                      },
                    )}
                    disabled={disabled}
                  >
                    {getFormDatePickerButtonLabel(mode, fieldValue)}
                  </Button>
                </FormControl>
              </PopoverTrigger>

              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode={mode}
                  selected={fieldValue}
                  onSelect={onSelect}
                  numberOfMonths={getNumberOfMonths(mode, isMobile)}
                  locale={fr}
                  defaultMonth={getDefaultMonthFromFieldValue(fieldValue)}
                  disabled={disabledDays}
                  {...props}
                  startMonth={new Date(1970, 0)}
                />
              </PopoverContent>
            </Popover>

            {hasMessage && fieldState.error ? (
              <FormMessage>{getRangeFieldErrorMessages(fieldState.error)}</FormMessage>
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}

export default FormDatePicker;
